import { emitterProps } from '../helpers/emitter-event'
import { Cart as CartType } from '../helpers/cart.types'

export class ConditionalCartContentElement extends HTMLElement {
  private cartCondition: 'empty' | 'not_empty' = 'empty'
  private cartCount: number = 1
  private originalContent: string = ''
  private isUpdating: boolean = false
  private updateTimeout: NodeJS.Timeout | null = null
  private cartUpdateHandler: (() => void) | null = null
  private isCurrentlyVisible: boolean | null = null
  private lastItemCount: number | null = null

  connectedCallback() {
    this.parseAttributes()
    this.storeOriginalContent()
    this.subscribeToCartUpdates()
    this.checkInitialCartState()
  }

  disconnectedCallback() {
    this.unsubscribeFromCartUpdates()
  }

  private parseAttributes() {
    const condition = this.getAttribute('data-cart-condition')
    if (condition === 'empty' || condition === 'not_empty') {
      this.cartCondition = condition
    }

    const count = this.getAttribute('data-cart-count')
    if (count) {
      this.cartCount = parseInt(count, 10) || 1
    }
  }

  private storeOriginalContent() {
    this.originalContent = this.innerHTML
  }

  private subscribeToCartUpdates() {
    this.cartUpdateHandler = () => {
      if (this.isUpdating) return

      if (this.updateTimeout) {
        clearTimeout(this.updateTimeout)
      }

      this.updateTimeout = setTimeout(() => {
        if (window.Cart && !this.isUpdating) {
          const currentCart = window.Cart.value
          if (currentCart && currentCart.item_count === this.lastItemCount) {
            return
          }

          this.isUpdating = true
          window.Cart.getCart()
            .then(() => {
              if (window.Cart?.value) {
                this.updateVisibility()
              }
            })
            .catch(() => {})
            .finally(() => {
              this.isUpdating = false
            })
        }
      }, 300)
    }

    const events = [
      ...Object.values(emitterProps.cart),
      'quantity-changed',
      'cart:updated',
      'cart:changed',
      'updateCartCount'
    ]

    events.forEach((event) => {
      document.addEventListener(event, this.cartUpdateHandler!)
    })
  }

  private unsubscribeFromCartUpdates() {
    if (this.cartUpdateHandler) {
      const events = [
        ...Object.values(emitterProps.cart),
        'quantity-changed',
        'cart:updated',
        'cart:changed',
        'updateCartCount'
      ]

      events.forEach((event) => {
        document.removeEventListener(event, this.cartUpdateHandler!)
      })
    }

    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout)
      this.updateTimeout = null
    }
  }

  private checkInitialCartState() {
    if (window.Cart?.value) {
      this.updateVisibility()
      return
    }

    const globalCart = (window as any).globals?.cart
    if (globalCart?.initCart) {
      this.evaluateCondition(globalCart.initCart)
      return
    }

    if (window.Cart) {
      window.Cart.getCart().then(() => {
        this.updateVisibility()
      }).catch(() => {
        this.hideContent()
      })
    } else {
      this.hideContent()
    }
  }

  private updateVisibility() {
    const cart = window.Cart?.value as CartType | undefined
    if (!cart) {
      const globalCart = (window as any).globals?.cart
      if (globalCart?.initCart) {
        this.evaluateCondition(globalCart.initCart)
        return
      }
      this.hideContent()
      return
    }

    this.evaluateCondition(cart)
  }

  private evaluateCondition(cart: CartType | any) {
    const itemCount = cart.item_count || 0
    let shouldShow = false

    if (this.cartCondition === 'empty') {
      shouldShow = itemCount === 0
    } else if (this.cartCondition === 'not_empty') {
      shouldShow = itemCount >= this.cartCount
    }

    if (this.isCurrentlyVisible === shouldShow && this.lastItemCount === itemCount) {
      return
    }

    this.lastItemCount = itemCount

    if (shouldShow) {
      this.showContent()
    } else {
      this.hideContent()
    }
  }

  private showContent() {
    if (this.isCurrentlyVisible === true) {
      return
    }

    this.style.display = ''
    this.style.visibility = ''
    this.removeAttribute('hidden')
    this.isCurrentlyVisible = true

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.reinitializeNestedComponents()
      })
    })
  }

  private reinitializeNestedComponents() {
    setTimeout(() => {
      const carousels = this.querySelectorAll('x-carousel')
      carousels.forEach((carousel: any) => {
        if (carousel.flickity) {
          try {
            carousel.flickity.resize()
            carousel.flickity.reloadCells()
          } catch {}
        }
      })

      const allElements = this.querySelectorAll('*')
      allElements.forEach((element: any) => {
        try {
          if (window.Flickity && (window as any).Flickity.data) {
            const flickityInstance = (window as any).Flickity.data(element)
            if (flickityInstance) {
              flickityInstance.resize()
              flickityInstance.reloadCells()
            }
          }
        } catch {}
      })

      const visibilityEvent = new CustomEvent('conditional-cart-content:visible', {
        bubbles: true,
        detail: { element: this }
      })
      this.dispatchEvent(visibilityEvent)

      window.dispatchEvent(new Event('resize'))
    }, 50)
  }

  private hideContent() {
    if (this.isCurrentlyVisible === false) {
      return
    }

    this.style.display = 'none'
    this.isCurrentlyVisible = false
  }
}
